import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { FirebaseError } from 'firebase/app';
import QRCode from 'qrcode';
import {
  getMultiFactorResolver,
  multiFactor,
  onAuthStateChanged,
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  TotpMultiFactorGenerator,
  TotpSecret,
  type MultiFactorInfo,
  type MultiFactorResolver,
  type User,
} from 'firebase/auth';
import { firebaseAuth } from '../lib/firebase';

const PENDING_MFA_ENROLLMENT_STORAGE_KEY = 'epson-dashboard.pending-mfa-enrollment';
const CREW_SESSION_STORAGE_KEY = 'epson-dashboard.crew-session';

const CREW_CREDENTIALS = {
  email: 'crew@epson.com',
  password: 'epson123#',
};

interface MfaEnrollmentDetails {
  ownerUid: string;
  accountName: string;
  issuer: string;
  qrCodeDataUrl: string | null;
  secretKey: string;
  sessionInfo: string;
  hashingAlgorithm: string;
  codeLength: number;
  codeIntervalSeconds: number;
  enrollmentCompletionDeadline: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isCrewUser: boolean;
  isLoading: boolean;
  isEmailVerified: boolean;
  isMfaEnrolled: boolean;
  isMfaChallengeRequired: boolean;
  requiresEmailVerification: boolean;
  requiresMfaSetup: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  refreshUser: () => Promise<void>;
  beginMfaEnrollment: () => Promise<void>;
  completeMfaEnrollment: (verificationCode: string) => Promise<void>;
  completeMfaChallenge: (verificationCode: string) => Promise<void>;
  cancelMfaChallenge: () => void;
  mfaEnrollment: MfaEnrollmentDetails | null;
  mfaHintLabel: string | null;
  mfaEnrolledFactorLabel: string | null;
  user: User | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

function readPendingMfaEnrollment() {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = window.sessionStorage.getItem(PENDING_MFA_ENROLLMENT_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as MfaEnrollmentDetails;
  } catch {
    window.sessionStorage.removeItem(PENDING_MFA_ENROLLMENT_STORAGE_KEY);
    return null;
  }
}

function writePendingMfaEnrollment(enrollment: MfaEnrollmentDetails | null) {
  if (typeof window === 'undefined') {
    return;
  }

  if (!enrollment) {
    window.sessionStorage.removeItem(PENDING_MFA_ENROLLMENT_STORAGE_KEY);
    return;
  }

  window.sessionStorage.setItem(
    PENDING_MFA_ENROLLMENT_STORAGE_KEY,
    JSON.stringify(enrollment),
  );
}

function getFriendlyAuthError(error: unknown) {
  if (!(error instanceof FirebaseError)) {
    return error instanceof Error ? error.message : 'Authentication failed.';
  }

  const serverMessage = (
    error.customData as { _serverResponse?: { error?: { message?: string } } } | undefined
  )?._serverResponse?.error?.message;

  if (serverMessage) {
    return `${error.message} (${serverMessage})`;
  }

  switch (error.code) {
    case 'auth/invalid-credential':
      return 'Invalid email or password.';
    case 'auth/invalid-email':
      return 'Enter a valid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a moment and try again.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    case 'auth/invalid-verification-code':
      return 'The authenticator code is invalid.';
    case 'auth/code-expired':
      return 'The authenticator code has expired. Enter a new code.';
    default:
      return error.message || 'Authentication failed.';
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isCrewUser, setIsCrewUser] = useState(() => {
    return localStorage.getItem(CREW_SESSION_STORAGE_KEY) === 'true';
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isMfaEnrolled, setIsMfaEnrolled] = useState(false);
  const [mfaEnrolledFactorLabel, setMfaEnrolledFactorLabel] = useState<string | null>(null);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [mfaEnrollmentSecret, setMfaEnrollmentSecret] = useState<TotpSecret | null>(null);
  const [mfaEnrollment, setMfaEnrollment] = useState<MfaEnrollmentDetails | null>(null);

  const updateMfaEnrollment = (enrollment: MfaEnrollmentDetails | null) => {
    setMfaEnrollment(enrollment);
    writePendingMfaEnrollment(enrollment);
  };

  const syncUserState = (nextUser: User | null) => {
    setUser(nextUser);
    setIsEmailVerified(Boolean(nextUser?.emailVerified));

    if (!nextUser) {
      setIsMfaEnrolled(false);
      setMfaEnrolledFactorLabel(null);
      return;
    }

    const totpFactors = multiFactor(nextUser).enrolledFactors.filter(
      (factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    );

    setIsMfaEnrolled(totpFactors.length > 0);
    setMfaEnrolledFactorLabel(totpFactors[0]?.displayName ?? 'Authenticator app');
  };

  const clearMfaFlow = () => {
    setMfaResolver(null);
    setMfaEnrollmentSecret(null);
    updateMfaEnrollment(null);
  };

  useEffect(() => {
    if (isCrewUser) {
      setIsLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(firebaseAuth, (nextUser) => {
      syncUserState(nextUser);
      setIsLoading(false);
    });

    return unsubscribe;
  }, [isCrewUser]);

  useEffect(() => {
    const storedEnrollment = readPendingMfaEnrollment();

    if (!user) {
      if (storedEnrollment) {
        writePendingMfaEnrollment(null);
      }
      return;
    }

    if (!storedEnrollment || storedEnrollment.ownerUid !== user.uid) {
      return;
    }

    const isExpired = Date.parse(storedEnrollment.enrollmentCompletionDeadline) <= Date.now();
    if (isExpired) {
      writePendingMfaEnrollment(null);
      setMfaEnrollmentSecret(null);
      setMfaEnrollment(null);
      return;
    }

    setMfaEnrollmentSecret(new TotpSecret(
      storedEnrollment.secretKey,
      storedEnrollment.hashingAlgorithm,
      storedEnrollment.codeLength,
      storedEnrollment.codeIntervalSeconds,
      storedEnrollment.enrollmentCompletionDeadline,
      storedEnrollment.sessionInfo,
      firebaseAuth,
    ));
    setMfaEnrollment(storedEnrollment);

    if (!storedEnrollment.qrCodeDataUrl) {
      const qrCodeUri = new TotpSecret(
        storedEnrollment.secretKey,
        storedEnrollment.hashingAlgorithm,
        storedEnrollment.codeLength,
        storedEnrollment.codeIntervalSeconds,
        storedEnrollment.enrollmentCompletionDeadline,
        storedEnrollment.sessionInfo,
        firebaseAuth,
      ).generateQrCodeUrl(storedEnrollment.accountName, storedEnrollment.issuer);

      void QRCode.toDataURL(qrCodeUri, {
        margin: 1,
        width: 240,
      })
        .then((qrCodeDataUrl) => {
          setMfaEnrollment((current) => {
            if (!current || current.ownerUid !== storedEnrollment.ownerUid) {
              return current;
            }

            const nextEnrollment = {
              ...current,
              qrCodeDataUrl,
            };

            writePendingMfaEnrollment(nextEnrollment);
            return nextEnrollment;
          });
        })
        .catch(() => {
          // Manual setup key is enough to complete TOTP enrollment.
        });
    }
  }, [user]);

  const login = async (email: string, password: string) => {
    clearMfaFlow();

    if (email === CREW_CREDENTIALS.email && password === CREW_CREDENTIALS.password) {
      setIsCrewUser(true);
      localStorage.setItem(CREW_SESSION_STORAGE_KEY, 'true');
      return;
    }

    try {
      await signInWithEmailAndPassword(firebaseAuth, email, password);
    } catch (error) {
      if (error instanceof FirebaseError && error.code === 'auth/multi-factor-auth-required') {
        setMfaResolver(getMultiFactorResolver(firebaseAuth, error));
        return;
      }

      throw new Error(getFriendlyAuthError(error));
    }
  };

  const logout = async () => {
    clearMfaFlow();
    if (isCrewUser) {
      setIsCrewUser(false);
      localStorage.removeItem(CREW_SESSION_STORAGE_KEY);
      return;
    }
    await signOut(firebaseAuth);
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(firebaseAuth, email);
    } catch (error) {
      throw new Error(getFriendlyAuthError(error));
    }
  };

  const sendVerificationEmailToUser = async () => {
    if (!user) {
      throw new Error('Sign in before requesting a verification email.');
    }

    try {
      await sendEmailVerification(user);
    } catch (error) {
      throw new Error(getFriendlyAuthError(error));
    }
  };

  const refreshUser = async () => {
    if (!firebaseAuth.currentUser) {
      syncUserState(null);
      return;
    }

    try {
      await reload(firebaseAuth.currentUser);
      syncUserState(firebaseAuth.currentUser);
    } catch (error) {
      throw new Error(getFriendlyAuthError(error));
    }
  };

  const beginMfaEnrollment = async () => {
    if (!user) {
      throw new Error('Sign in before setting up multi-factor authentication.');
    }

    if (!user.emailVerified) {
      throw new Error('Verify your email address before setting up multi-factor authentication.');
    }

    try {
      const session = await multiFactor(user).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(session);
      const accountName = user.email ?? user.uid;
      const issuer = 'Epson Dashboard';
      const qrCodeUri = secret.generateQrCodeUrl(accountName, issuer);
      const secretKey = (secret as TotpSecret & { secretKey?: string }).secretKey ?? '';
      const enrollmentCompletionDeadline = (secret as TotpSecret & { enrollmentCompletionDeadline?: string }).enrollmentCompletionDeadline
        ?? new Date(Date.now() + 10 * 60 * 1000).toUTCString();
      const sessionInfo = (secret as TotpSecret & { sessionInfo?: string }).sessionInfo ?? '';
      const hashingAlgorithm = (secret as TotpSecret & { hashingAlgorithm?: string }).hashingAlgorithm ?? 'SHA1';
      const codeLength = (secret as TotpSecret & { codeLength?: number }).codeLength ?? 6;
      const codeIntervalSeconds = (secret as TotpSecret & { codeIntervalSeconds?: number }).codeIntervalSeconds ?? 30;

      setMfaEnrollmentSecret(secret);
      updateMfaEnrollment({
        ownerUid: user.uid,
        accountName,
        issuer,
        qrCodeDataUrl: null,
        secretKey,
        sessionInfo,
        hashingAlgorithm,
        codeLength,
        codeIntervalSeconds,
        enrollmentCompletionDeadline,
      });

      void QRCode.toDataURL(qrCodeUri, {
        margin: 1,
        width: 240,
      })
        .then((qrCodeDataUrl) => {
          setMfaEnrollment((current) => {
            if (!current || current.secretKey !== secretKey || current.ownerUid !== user.uid) {
              return current;
            }

            const nextEnrollment = {
              ...current,
              qrCodeDataUrl,
            };

            writePendingMfaEnrollment(nextEnrollment);
            return nextEnrollment;
          });
        })
        .catch(() => {
          // Manual setup key is enough to complete TOTP enrollment.
        });
    } catch (error) {
      throw new Error(getFriendlyAuthError(error));
    }
  };

  const completeMfaEnrollment = async (verificationCode: string) => {
    if (!user) {
      throw new Error('Sign in before setting up multi-factor authentication.');
    }

    if (!mfaEnrollmentSecret) {
      throw new Error('Start the authenticator setup before submitting a code.');
    }

    const normalizedCode = verificationCode.trim();
    if (!normalizedCode) {
      throw new Error('Enter the code from your authenticator app.');
    }

    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        mfaEnrollmentSecret,
        normalizedCode,
      );

      await multiFactor(user).enroll(assertion, 'Authenticator app');
      clearMfaFlow();
      await refreshUser();
    } catch (error) {
      throw new Error(getFriendlyAuthError(error));
    }
  };

  const completeMfaChallenge = async (verificationCode: string) => {
    if (!mfaResolver) {
      throw new Error('Sign in again to continue the multi-factor challenge.');
    }

    const normalizedCode = verificationCode.trim();
    if (!normalizedCode) {
      throw new Error('Enter the code from your authenticator app.');
    }

    const factor = mfaResolver.hints.find(
      (hint: MultiFactorInfo) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    );

    if (!factor) {
      throw new Error('No TOTP factor is available for this account.');
    }

    try {
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(factor.uid, normalizedCode);
      const credential = await mfaResolver.resolveSignIn(assertion);

      clearMfaFlow();
      syncUserState(credential.user);
    } catch (error) {
      throw new Error(getFriendlyAuthError(error));
    }
  };

  const cancelMfaChallenge = () => {
    clearMfaFlow();
  };

  const mfaHintLabel = mfaResolver?.hints[0]?.displayName ?? 'Authenticator app';
  const requiresEmailVerification = !!user && !isEmailVerified && !isCrewUser;
  const requiresMfaSetup = !!user && isEmailVerified && !isMfaEnrolled && !isCrewUser;

  const value = useMemo<AuthContextType>(() => ({
    isAuthenticated: !!user || isCrewUser,
    isCrewUser,
    isLoading,
    isEmailVerified: isEmailVerified || isCrewUser,
    isMfaEnrolled: isMfaEnrolled || isCrewUser,
    isMfaChallengeRequired: !!mfaResolver,
    requiresEmailVerification,
    requiresMfaSetup,
    login,
    logout,
    resetPassword,
    sendVerificationEmail: sendVerificationEmailToUser,
    refreshUser,
    beginMfaEnrollment,
    completeMfaEnrollment,
    completeMfaChallenge,
    cancelMfaChallenge,
    mfaEnrollment,
    mfaHintLabel,
    mfaEnrolledFactorLabel,
    user,
  }), [
    isCrewUser,
    isEmailVerified,
    isLoading,
    isMfaEnrolled,
    mfaEnrollment,
    mfaEnrolledFactorLabel,
    mfaHintLabel,
    mfaResolver,
    requiresEmailVerification,
    requiresMfaSetup,
    user,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
